/**
 * PlayerNetworkGraph · Phase A · Schritt 3+ · Tactical Insights.
 *
 * Force-directed graph of the team's passing hierarchy. Each node is a
 * tracked Player_ID; each edge is a verified throw between two players.
 *
 *   Node radius     ∝ √(total throws + catches) — square-root so a star
 *                     handler with 50 throws doesn't drown out an
 *                     occasional cutter with 3 receptions
 *   Edge thickness  ∝ pass count
 *   Edge colour     = primary cyan, opacity ∝ count too
 *
 * Layout algorithm: lightweight ad-hoc force simulation (Verlet-style
 * integration) that runs for ~300 ticks at mount and again whenever
 * the graph topology changes. We deliberately avoid pulling in d3-force
 * or react-force-graph — 30 KB of dependency for a 50-line simulation
 * loop is the wrong trade-off given our typical N=10..30 players.
 *
 * Interactions:
 *   - Hover a node → highlight that player's adjacent edges + show
 *     a tooltip with throw/catch counts.
 *   - Click a node → toggle "focus mode" that dims everything else.
 *   - Match filter (top-right dropdown) when ≥2 matches contribute.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Network, Loader2, Users, MoveDiagonal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { fehlerText, fetchPlayerNetwork } from "@/lib/api";

const WIDTH  = 720;
const HEIGHT = 460;
const SIM_TICKS = 320;
const NODE_BASE_R = 14;
const NODE_GROWTH = 6;   // pixels added per √(total)

export default function PlayerNetworkGraph() {
  const [data, setData] = useState({ nodes: [], edges: [], matches: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeMatch, setActiveMatch] = useState("__all__");
  const [hoverId, setHoverId] = useState(null);
  const [focusId, setFocusId] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const matchId = activeMatch === "__all__" ? undefined : activeMatch;
        const payload = await fetchPlayerNetwork(matchId);
        if (!alive) return;
        setData(payload);
        setError(null);
      } catch (e) {
        if (!alive) return;
        setError(fehlerText(e) || "Could not load player network");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [activeMatch]);

  // Run the simulation when topology changes. `positions` is the only
  // stateful output — keyed by node.id so React can interpolate stably
  // even if the upstream payload reorders.
  const positions = useMemo(
    () => simulateLayout(data.nodes, data.edges, { width: WIDTH, height: HEIGHT }),
    [data.nodes, data.edges],
  );

  const maxEdgeCount = useMemo(
    () => Math.max(1, ...data.edges.map((e) => e.count)),
    [data.edges],
  );
  const maxNodeTotal = useMemo(
    () => Math.max(1, ...data.nodes.map((n) => n.total)),
    [data.nodes],
  );

  const focusSet = useMemo(() => {
    if (!focusId) return null;
    const adj = new Set([focusId]);
    for (const e of data.edges) {
      if (e.from === focusId) adj.add(e.to);
      if (e.to   === focusId) adj.add(e.from);
    }
    return adj;
  }, [focusId, data.edges]);

  const hasData = data.nodes.length > 0;
  const showLegend = data.matches.length > 1;

  return (
    <div
      data-testid="player-network-graph"
      className="border border-border bg-card rounded-sm overflow-hidden"
    >
      {/* Header */}
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-border bg-secondary/30">
        <div className="flex items-center gap-2">
          <Network className="w-4 h-4 text-primary" />
          <div>
            <div className="font-display font-black uppercase tracking-widest text-xs text-primary">
              Player Network
            </div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mt-0.5">
              {data.nodes.length} players · {data.edges.length} pass connection{data.edges.length === 1 ? "" : "s"}
            </div>
          </div>
        </div>
        {showLegend && (
          <select
            data-testid="player-network-match-filter"
            value={activeMatch}
            onChange={(e) => { setActiveMatch(e.target.value); setFocusId(null); }}
            className="bg-secondary border border-border rounded-sm px-2 py-1 font-mono text-[10px] uppercase tracking-widest text-foreground focus:outline-none focus:border-primary"
          >
            <option value="__all__">All matches</option>
            {data.matches.map((m) => (
              <option key={m.id} value={m.id}>{m.name?.slice(0, 30) || m.id.slice(0, 8)}</option>
            ))}
          </select>
        )}
      </div>

      {/* Canvas */}
      <div className="relative">
        {loading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-card/60">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        )}
        {error && (
          <div className="absolute inset-0 z-10 flex items-center justify-center p-6 bg-card/80">
            <div className="font-mono text-xs text-rose-300">{error}</div>
          </div>
        )}
        {!loading && !error && !hasData && (
          <div className="flex flex-col items-center justify-center text-center gap-3 px-6 py-12 text-muted-foreground">
            <Users className="w-8 h-8 opacity-50" />
            <div className="font-mono text-[11px] uppercase tracking-widest">
              No verified pass-vectors yet
            </div>
            <p className="font-mono text-[10px] text-muted-foreground/80 max-w-sm normal-case tracking-normal">
              Once the coach accepts heuristic proposals in <strong>Sequence Lab → Pass-Vector Triage</strong>,
              the verified throws will populate this network graph automatically.
              Connections appear only when both thrower and catcher Player_IDs are present.
            </p>
          </div>
        )}

        {hasData && (
          <svg
            viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
            className="block w-full h-auto"
            style={{ minHeight: 320 }}
            onClick={() => setFocusId(null)}
          >
            <defs>
              <marker
                id="player-network-arrow" viewBox="0 0 10 10" refX="9" refY="5"
                markerWidth="4" markerHeight="4" orient="auto-start-reverse"
              >
                <path d="M 0 0 L 10 5 L 0 10 Z" fill="currentColor" />
              </marker>
            </defs>

            {/* Edges (under nodes) */}
            <g>
              {data.edges.map((e) => {
                const p1 = positions[e.from];
                const p2 = positions[e.to];
                if (!p1 || !p2) return null;
                const r1 = nodeRadius(maxNodeTotal, data.nodes.find((n) => n.id === e.from)?.total ?? 0);
                const r2 = nodeRadius(maxNodeTotal, data.nodes.find((n) => n.id === e.to)?.total ?? 0);
                // Shorten the edge endpoints so the arrowhead doesn't tuck under the node.
                const dx = p2.x - p1.x, dy = p2.y - p1.y;
                const len = Math.hypot(dx, dy) || 1;
                const ux = dx / len, uy = dy / len;
                const sx = p1.x + ux * r1;
                const sy = p1.y + uy * r1;
                const ex = p2.x - ux * (r2 + 4);
                const ey = p2.y - uy * (r2 + 4);

                const weight = 1 + (e.count / maxEdgeCount) * 4;
                const baseOpacity = 0.35 + (e.count / maxEdgeCount) * 0.5;
                const focused = !focusSet || focusSet.has(e.from) && focusSet.has(e.to);
                const hovered = hoverId === e.from || hoverId === e.to;
                const opacity = focused ? (hovered ? 1.0 : baseOpacity) : 0.08;
                const stroke = hovered ? "#FCD34D" : "#22d3ee";

                return (
                  <g key={e.id} className="text-cyan-400" data-testid={`network-edge-${e.id}`}>
                    <line
                      x1={sx} y1={sy} x2={ex} y2={ey}
                      stroke={stroke}
                      strokeWidth={weight}
                      strokeLinecap="round"
                      opacity={opacity}
                      markerEnd="url(#player-network-arrow)"
                    />
                    {hovered && (
                      <text
                        x={(sx + ex) / 2}
                        y={(sy + ey) / 2 - 6}
                        textAnchor="middle"
                        className="font-mono fill-amber-300"
                        style={{ fontSize: 9 }}
                      >
                        {e.count}× · ⌀ {e.avg_length_m} m
                      </text>
                    )}
                  </g>
                );
              })}
            </g>

            {/* Nodes (above edges) */}
            <g>
              {data.nodes.map((n) => {
                const p = positions[n.id];
                if (!p) return null;
                const r = nodeRadius(maxNodeTotal, n.total);
                const focused = !focusSet || focusSet.has(n.id);
                const opacity = focused ? 1 : 0.18;
                const isHover = hoverId === n.id;
                const isFocus = focusId === n.id;
                return (
                  <g
                    key={n.id}
                    transform={`translate(${p.x}, ${p.y})`}
                    onMouseEnter={() => setHoverId(n.id)}
                    onMouseLeave={() => setHoverId(null)}
                    onClick={(ev) => { ev.stopPropagation(); setFocusId(isFocus ? null : n.id); }}
                    style={{ cursor: "pointer", opacity }}
                    data-testid={`network-node-${n.id}`}
                  >
                    <circle
                      r={r}
                      fill={isHover || isFocus ? "#FCD34D" : "#0a0a0a"}
                      stroke={isHover || isFocus ? "#fde047" : "#22d3ee"}
                      strokeWidth={isHover || isFocus ? 3 : 2}
                    />
                    <text
                      textAnchor="middle"
                      dy="0.35em"
                      className="font-display font-black"
                      fill={isHover || isFocus ? "#0a0a0a" : "#e5e7eb"}
                      style={{ fontSize: Math.max(10, r * 0.7), pointerEvents: "none" }}
                    >
                      {n.label}
                    </text>
                    {/* Phase D · Re-ID glyph — small emerald 🧬 on
                        the upper-right of every node whose underlying
                        clip was Re-ID-stitched. Tooltip on hover
                        surfaces the raw member pids. */}
                    {n.stitched && (
                      <g
                        data-testid={`network-node-${n.id}-stitched-glyph`}
                        transform={`translate(${r * 0.7}, ${-r * 0.7})`}
                        style={{ pointerEvents: "none" }}
                      >
                        <circle r={5} fill="#10b981" stroke="#022c22" strokeWidth={1} />
                        <text
                          x={0} y={0} dy="0.32em" textAnchor="middle"
                          style={{ fontSize: 6, fontWeight: 900 }}
                          fill="#022c22"
                        >🧬</text>
                      </g>
                    )}
                    {(isHover || isFocus) && (() => {
                      // Phase D · auto-size the hover tooltip so the
                      // stitched-from text doesn't overflow past the
                      // cyan border. Width is derived from the
                      // longest line (~5.5 px per char at fontSize=8).
                      const memberText = n.stitched && n.member_pids?.length > 1
                        ? `🧬 stitched from #${n.member_pids.join(", #")}`
                        : null;
                      const baseText = `${n.throws}↗ · ${n.catches}↙ · Σ${n.total}`;
                      const maxLen = Math.max(
                        baseText.length,
                        memberText ? memberText.length : 0,
                      );
                      // 6 px / char at fontSize=8 monospace + 16 px padding
                      const ttWidth = Math.max(92, Math.round(maxLen * 6) + 16);
                      const ttHeight = memberText ? 32 : 20;
                      return (
                      <g transform={`translate(0, ${-r - 14})`} style={{ pointerEvents: "none" }}>
                        <rect
                          x={-ttWidth / 2} y={-12}
                          width={ttWidth}
                          height={ttHeight}
                          fill="#0a0a0a" stroke="#22d3ee" rx={3}
                        />
                        <text
                          x={0} y={2}
                          textAnchor="middle"
                          className="font-mono fill-cyan-300"
                          style={{ fontSize: 9 }}
                        >
                          {baseText}
                        </text>
                        {memberText && (
                          <text
                            x={0} y={14}
                            textAnchor="middle"
                            className="font-mono fill-emerald-300"
                            style={{ fontSize: 8 }}
                          >
                            {memberText}
                          </text>
                        )}
                      </g>
                      );
                    })()}
                  </g>
                );
              })}
            </g>
          </svg>
        )}
      </div>

      {/* Footer hint */}
      {hasData && (
        <div className="px-4 py-2 border-t border-border bg-secondary/30 flex items-center justify-between font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <MoveDiagonal className="w-3 h-3" />
            Node size ∝ √(throws + catches) · edge thickness ∝ pass count
          </span>
          {focusId && (
            <Button
              size="sm" variant="ghost" onClick={() => setFocusId(null)}
              className="h-6 px-2 font-mono text-[10px] uppercase tracking-widest hover:bg-accent hover:text-primary"
              data-testid="player-network-clear-focus"
            >
              Clear focus
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────
function nodeRadius(maxTotal, total) {
  if (!total) return NODE_BASE_R;
  return NODE_BASE_R + NODE_GROWTH * Math.sqrt(total / maxTotal) * 1.5;
}

/**
 * Lightweight Verlet-style force simulation for small graphs (N≤30).
 *
 * Three forces:
 *   1. SPRING along every edge — target distance scales with √(weight).
 *   2. CHARGE between every pair of nodes — Coulomb-style 1/r repulsion.
 *   3. CENTER pull toward the canvas mid — keeps disconnected clusters
 *      from drifting off-screen.
 *
 * 320 iterations are enough to converge for our typical density without
 * adding a stop condition that could explode on degenerate inputs.
 */
function simulateLayout(nodes, edges, { width, height }) {
  if (!nodes.length) return {};
  const cx = width / 2, cy = height / 2;
  // Seed positions: golden-angle spiral so degenerate-input layouts
  // still spread visibly even before forces kick in.
  const golden = Math.PI * (3 - Math.sqrt(5));
  const positions = {};
  nodes.forEach((n, i) => {
    const r = Math.min(width, height) * 0.25;
    positions[n.id] = {
      x: cx + r * Math.cos(i * golden + 0.7),
      y: cy + r * Math.sin(i * golden + 0.7),
      vx: 0, vy: 0,
    };
  });

  const nodeIds = nodes.map((n) => n.id);
  const edgeList = edges.filter((e) => positions[e.from] && positions[e.to]);
  const SPRING_K = 0.012;
  const CHARGE_K = 480;
  const CENTER_K = 0.005;
  const DAMP = 0.85;

  for (let tick = 0; tick < SIM_TICKS; tick++) {
    // Pairwise charge (O(N^2) — fine for N≤30).
    for (let i = 0; i < nodeIds.length; i++) {
      const a = positions[nodeIds[i]];
      for (let j = i + 1; j < nodeIds.length; j++) {
        const b = positions[nodeIds[j]];
        const dx = b.x - a.x, dy = b.y - a.y;
        const d2 = dx * dx + dy * dy + 0.1;
        const d  = Math.sqrt(d2);
        const f  = CHARGE_K / d2;
        const fx = f * dx / d, fy = f * dy / d;
        a.vx -= fx; a.vy -= fy;
        b.vx += fx; b.vy += fy;
      }
    }
    // Spring along each edge.
    for (const e of edgeList) {
      const a = positions[e.from], b = positions[e.to];
      const dx = b.x - a.x, dy = b.y - a.y;
      const d  = Math.hypot(dx, dy) || 0.1;
      const target = 120 + 8 * Math.sqrt(e.count);
      const delta  = d - target;
      const fx = SPRING_K * delta * dx / d;
      const fy = SPRING_K * delta * dy / d;
      a.vx += fx; a.vy += fy;
      b.vx -= fx; b.vy -= fy;
    }
    // Centring pull.
    for (const id of nodeIds) {
      const p = positions[id];
      p.vx += (cx - p.x) * CENTER_K;
      p.vy += (cy - p.y) * CENTER_K;
      p.vx *= DAMP; p.vy *= DAMP;
      p.x += p.vx;  p.y += p.vy;
    }
  }

  // Clamp to viewport (with padding).
  const pad = 28;
  for (const id of nodeIds) {
    const p = positions[id];
    p.x = Math.max(pad, Math.min(width  - pad, p.x));
    p.y = Math.max(pad, Math.min(height - pad, p.y));
  }
  return positions;
}
